<div class="report-popup-container" [class.dark-theme]="isDark" [class.light-theme]="!isDark">

  <!-- MODAL HEADER -->
  <div class="popup-header">
    <div class="header-left">
      <div class="header-icon-box">
        <mat-icon>assessment</mat-icon>
      </div>
      <div>
        <div class="header-title-row">
          <h2>Voyage Report Details</h2>
          <span class="badge-status" [ngClass]="{
            'status-verified': data?.report?.verified && !data?.report?.force_submission,
            'status-force': data?.report?.force_submission,
            'status-draft': !data?.report?.verified
          }">
            {{ data?.report?.force_submission ? 'Force Submission' : (data?.report?.verified ? 'Verified' : (data?.report?.reportStatus || 'Draft')) }}
          </span>
        </div>
        <div class="header-subtitle">
          <span *ngIf="getVesselName()"><strong>{{ getVesselName() }}</strong> <span *ngIf="getImo()">(IMO: {{ getImo() }})</span></span>
          <span class="dot-separator" *ngIf="getVoyageNumber()">•</span>
          <span *ngIf="getVoyageNumber()">Voyage: <strong>#{{ getVoyageNumber() }}</strong></span>
          <span class="dot-separator" *ngIf="getOperations()">•</span>
          <span *ngIf="getOperations()" class="badge-event">{{ getOperations() }}</span>
          <span class="dot-separator" *ngIf="getReportDateTime()">•</span>
          <span *ngIf="getReportDateTime()" class="utc-badge">{{ getReportDateTime() }}</span>
        </div>
      </div>
    </div>

    <div class="header-right">
      <div class="search-box">
        <mat-icon class="search-icon">search</mat-icon>
        <input
          type="text"
          placeholder="Filter fields..."
          [(ngModel)]="searchTerm"
          class="search-input"
        />
        <mat-icon
          class="clear-search-icon"
          *ngIf="searchTerm"
          (click)="searchTerm = ''"
        >close</mat-icon>
      </div>

      <button
        mat-icon-button
        class="btn-theme-toggle"
        (click)="toggleTheme()"
        [matTooltip]="isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'"
      >
        <mat-icon>{{ isDark ? 'light_mode' : 'dark_mode' }}</mat-icon>
      </button>

      <button mat-icon-button class="btn-close" (click)="closeModal()" matTooltip="Close">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </div>

  <!-- POPUP BODY -->
  <div class="popup-body">

    <!-- REPORT SECTIONS IN EXACT HTML FORM ORDER -->
    <ng-container *ngFor="let cat of getReportCategories()">
      <div class="section-card" [id]="'section-' + cat.id">
        <div class="section-header">
          <div class="section-title-wrap">
            <mat-icon class="section-icon">{{ cat.icon }}</mat-icon>
            <h3 class="section-title">{{ cat.title }}</h3>
          </div>
          <span class="section-count-badge">{{ cat.fields.length }} parameters</span>
        </div>

        <div class="fields-grid">
          <div class="field-item" *ngFor="let item of cat.fields">
            <span class="field-label">{{ item.label }}:</span>
            <span class="field-value" [title]="formatValue(item.key, item.value)">{{ formatValue(item.key, item.value) }}</span>
          </div>
        </div>

        <!-- SPECIAL INTEGRATED TABLE FOR CARGO BREAKDOWN IN CARGO SECTION -->
        <div class="fuel-table-wrapper" *ngIf="cat.id === 'cargo' && hasCargoDetails()">
          <h4 class="sub-table-title">
            <mat-icon>inventory</mat-icon>
            Detailed Cargo Breakdown
          </h4>
          <div class="table-responsive">
            <table class="data-table">
              <thead>
                <tr>
                  <th style="text-align: left;">Cargo Name / Grade</th>
                  <th>Quantity (MT)</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let cargo of getCargoDetails()">
                  <td class="fuel-type-cell" style="text-align: left;"><strong>{{ cargo.name || 'Cargo' }}</strong></td>
                  <td class="total-cons-cell">{{ formatValue('cargoQty', cargo.quantity ?? cargo.qty) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <!-- SPECIAL INTEGRATED TABLE FOR FUEL OIL DATA IN FUEL SECTION -->
        <div class="fuel-table-wrapper" *ngIf="cat.id === 'fuels' && (data?.report?.fuelOilData?.length || hasMgoData())">
          <h4 class="sub-table-title">
            <mat-icon>opacity</mat-icon>
            Detailed Fuel Breakdown
          </h4>
          <div class="table-responsive">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Fuel Grade</th>
                  <th>ME Cons (MT)</th>
                  <th>AE Cons (MT)</th>
                  <th>Boiler Cons (MT)</th>
                  <th>Total Cons (MT)</th>
                  <th>Bunker ROB (MT)</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let fuel of data?.report?.fuelOilData">
                  <td class="fuel-type-cell"><strong>{{ fuel.fuelType }}</strong></td>
                  <td>{{ fuel.meConsumption ?? '-' }}</td>
                  <td>{{ fuel.aeConsumption ?? '-' }}</td>
                  <td>{{ fuel.blrConsumption ?? '-' }}</td>
                  <td class="total-cons-cell">{{ fuel.totalConsumption ?? '-' }}</td>
                  <td>{{ fuel.bunkerRob ?? '-' }}</td>
                </tr>
                <tr *ngIf="hasMgoData()" class="mgo-row">
                  <td class="fuel-type-cell"><strong>MGO</strong></td>
                  <td>{{ getValue('mgoMeConsumption') }}</td>
                  <td>{{ getValue('mgoAeConsumption') }}</td>
                  <td>{{ getValue('mgoBlrConsumption') }}</td>
                  <td class="total-cons-cell">{{ getValue('mgoTotalConsumption') }}</td>
                  <td>{{ getMgoRob() }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </ng-container>

    <!-- LAST REPORT (IF AVAILABLE) -->
    <div class="section-card last-report-card" *ngIf="data?.last_report && getLastReportFields().length > 0">
      <div class="section-header">
        <div class="section-title-wrap">
          <mat-icon class="section-icon">history</mat-icon>
          <h3 class="section-title">Last Verified Report</h3>
        </div>
        <span class="section-count-badge">{{ getLastReportFields().length }} parameters</span>
      </div>

      <div class="fields-grid">
        <div class="field-item" *ngFor="let item of getLastReportFields()">
          <span class="field-label">{{ item.label }}:</span>
          <span class="field-value" [title]="formatValue(item.key, item.value)">{{ formatValue(item.key, item.value) }}</span>
        </div>
      </div>

      <!-- SPECIAL INTEGRATED TABLE FOR CARGO BREAKDOWN IN LAST REPORT -->
      <div class="fuel-table-wrapper" *ngIf="hasCargoDetails(data?.last_report)">
        <h4 class="sub-table-title">
          <mat-icon>inventory</mat-icon>
          Detailed Cargo Breakdown (Last Report)
        </h4>
        <div class="table-responsive">
          <table class="data-table">
            <thead>
              <tr>
                <th style="text-align: left;">Cargo Name / Grade</th>
                <th>Quantity (MT)</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let cargo of getCargoDetails(data?.last_report)">
                <td class="fuel-type-cell" style="text-align: left;"><strong>{{ cargo.name || 'Cargo' }}</strong></td>
                <td class="total-cons-cell">{{ formatValue('cargoQty', cargo.quantity ?? cargo.qty) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- EMPTY FILTER RESULTS -->
    <div class="empty-filter-state" *ngIf="getReportCategories().length === 0">
      <mat-icon>search_off</mat-icon>
      <h4>No fields matching "{{ searchTerm }}"</h4>
      <p>Try searching with another keyword or clear the filter.</p>
      <button class="btn-clear-filter" (click)="searchTerm = ''">Clear Search</button>
    </div>

  </div>

</div>
